/**
 * Prefetch Interactive Map Data
 *
 * This function makes the same API call as the Interactive Map page without storing the result.
 * It populates the HTTP cache so that when the user navigates to the map page,
 * the data loads instantly from cache instead of making a fresh API call.
 *
 * Uses the optimized custom API endpoint (/your_tour_api/map/{urlSlug})
 * which returns consolidated map data including SVG, levels, and status options.
 *
 * Usage:
 * - Call from HomePage after initial render
 * - Non-blocking: Doesn't affect current page performance
 */

import { buildYourTourApiUrl, fetchYourTourApi, getCurrentPlatform } from '../../utils/ApiHelpers';

let prefetchInProgress = false;
let prefetchComplete = false;
let hasUserVisitedInteractiveMap = false;

// Call this from InteractiveMapPage when it loads to mark that user has visited
export function markInteractiveMapVisited() {
  hasUserVisitedInteractiveMap = true;
}

export async function prefetchInteractiveMap(urlSlug, mode) {
  // Don't prefetch if user has already visited (already cached)
  if (hasUserVisitedInteractiveMap) {
    return;
  }

  // Don't prefetch if already in progress or complete
  if (prefetchInProgress || prefetchComplete) {
    return;
  }

  // Don't prefetch if required params are missing
  if (!urlSlug) {
    return;
  }

  prefetchInProgress = true;

  try {
    // Use the optimized map API endpoint
    // Endpoint always includes occupancy data for performance
    const params = mode ? { mode } : {};
    const url = buildYourTourApiUrl(`/your_tour_api/map/${urlSlug}`, params);

    await fetchYourTourApi(url, getCurrentPlatform());

    prefetchComplete = true;
  } catch (error) {
    console.warn('[Prefetch] Interactive map prefetch failed (non-critical):', error);
  } finally {
    prefetchInProgress = false;
  }
}

// Reset prefetch state (useful for testing or when community changes)
export function resetInteractiveMapPrefetch() {
  prefetchInProgress = false;
  prefetchComplete = false;
  hasUserVisitedInteractiveMap = false;
}
